/** Clientes: formulario + lista con busqueda y estados. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';
import { MobileCard } from '@/components/MobileCard';
import { SectionCard } from '@/components/SectionCard';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useCreateClientMutation, useMobileClientsQuery } from '@/hooks/useMobileClients';
import { mobileClientSchema, type MobileClientForm } from '@/schemas/client.schemas';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { RUC_SCOPE_HINT } from '@/utils/peruRuc';
import { hapticSuccess } from '@/utils/haptic';

export function ClientsPage() {
  const { user, hasPermission } = useMobileAuth();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const successTimer = useRef<number | null>(null);
  const clientsQuery = useMobileClientsQuery();
  const createMutation = useCreateClientMutation();
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => clientsQuery.refetch(),
    disabled: user?.active_tenant_id == null || !hasPermission('clients.view'),
  });
  useEffect(
    () => () => {
      if (successTimer.current !== null) window.clearTimeout(successTimer.current);
    },
    [],
  );
  const canCreate = hasPermission('clients.create');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MobileClientForm>({
    resolver: yupResolver(mobileClientSchema),
    defaultValues: { legal_name: '', tax_id: '', business_name: '' },
  });

  const filtered = useMemo(() => {
    const items = clientsQuery.data?.items ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (c) => c.legal_name.toLowerCase().includes(q) || (c.tax_id ?? '').includes(q),
    );
  }, [clientsQuery.data, search]);

  async function onSubmit(values: MobileClientForm) {
    setGeneralError(null);
    setSuccessMessage(null);
    try {
      await createMutation.mutateAsync({
        legal_name: values.legal_name.trim(),
        tax_id: values.tax_id.trim(),
        business_name: values.business_name?.trim() || undefined,
      });
      reset();
      hapticSuccess();
      setSuccessMessage('Cliente creado correctamente');
      if (successTimer.current !== null) window.clearTimeout(successTimer.current);
      successTimer.current = window.setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    }
  }

  return (
    <MobileLayout title="Clientes" subtitle={`${clientsQuery.data?.total ?? 0} en total`}>
      <div className="space-y-4">
        {canCreate && (
          <SectionCard title="Nuevo cliente">
            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-3" noValidate>
              <MobileInput
                label="Razón social"
                autoComplete="organization"
                enterKeyHint="next"
                placeholder="Ej: Constructora Andina SAC"
                staticLabel
                isRequired
                icon={<MobileIcon name="business" className="h-5 w-5" />}
                error={errors.legal_name?.message}
                {...register('legal_name')}
              />
              <MobileInput
                label="RUC"
                inputMode="numeric"
                autoComplete="off"
                enterKeyHint="next"
                maxLength={11}
                placeholder="Ej: 20123456786"
                staticLabel
                isRequired
                help={RUC_SCOPE_HINT}
                icon={<MobileIcon name="badge" className="h-5 w-5" />}
                error={errors.tax_id?.message}
                {...register('tax_id')}
              />
              <MobileInput
                label="Nombre comercial"
                autoComplete="organization"
                enterKeyHint="done"
                placeholder="Ej: Andina (opcional)"
                staticLabel
                icon={<MobileIcon name="store" className="h-5 w-5" />}
                error={errors.business_name?.message}
                {...register('business_name')}
              />
              {generalError && (
                <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
                  <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
                  <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
                </div>
              )}
              {successMessage && (
                <div className="flex items-center gap-2 rounded-2xl bg-success-light px-4 py-3">
                  <MobileIcon name="check_circle" className="h-5 w-5 shrink-0 text-success" />
                  <p className="min-w-0 flex-1 break-words text-sm text-success-dark">{successMessage}</p>
                </div>
              )}
              <MobileButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                loading={isSubmitting || createMutation.isPending}
                icon={<MobileIcon name="person_add" className="h-5 w-5" />}
              >
                Crear cliente
              </MobileButton>
            </form>
          </SectionCard>
        )}

        <div ref={bindRef}>
        <PullIndicator pulling={pulling} refreshing={refreshing} />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar por razón social o RUC..."
        />

        {clientsQuery.isPending && <CardListSkeleton count={4} />}

        {clientsQuery.isError && (
          <EmptyState
            icon="error"
            title="No se pudo cargar"
            description="Verifica tu conexión e intenta de nuevo."
            actionLabel="Reintentar"
            onAction={() => clientsQuery.refetch()}
          />
        )}

        {!clientsQuery.isPending && !clientsQuery.isError && filtered.length === 0 && (
          <EmptyState
            icon={search ? 'search_off' : 'contacts'}
            title={search ? 'Sin resultados' : 'Sin clientes'}
            description={search ? 'Ningún cliente coincide con tu búsqueda.' : 'Aún no hay clientes registrados.'}
          />
        )}

        <div className="space-y-3">
          {filtered.map((client) => (
            <MobileCard
              key={client.id}
              title={client.legal_name}
              subtitle={client.business_name ?? undefined}
              icon="person"
              iconColor="primary"
              metadata={[
                ...(client.tax_id ? [{ label: 'RUC', value: client.tax_id }] : []),
              ]}
            />
          ))}
        </div>
        </div>
      </div>
    </MobileLayout>
  );
}
